import { defineComponent as _defineComponent } from 'vue'
import { toRefs, useContext, ref, watch, onMounted } from "@nuxtjs/composition-api";
import { useMobile } from "~/core/composables/useMobile";
import { toCurrencyFormat } from "~/core/helpers/GlobalHelpers";
import { PaymentSpecialConditions } from "~/core/types/purchase-popup-2/PaymentSpecialConditions";
import { PaymentMethodOption } from "~/core/types/purchase-popup-2/PaymentMethodOption";
import { PaymentMethodEnum } from "~/core/types/purchase-popup-2/PaymentMethodEnum";
import { MetaUser } from "~/core/models/MetaUser";
import { UserBalance } from "~/core/models/purchase-popup-2/UserBalance";
import { OrderCurrency } from "~/pages/finances/wallet/constants/OrderCurrency";
import MunicipalityTx from "~/core/models/purchase-popup-2/MunicipalityTx";
import Web3MunicipalityTx from "~/core/models/purchase-popup-2/Web3MunicipalityTx";

interface Props {
    user: MetaUser,
    userBalance: UserBalance,
    transactionObject: MunicipalityTx | Web3MunicipalityTx,
    initiallyChosenTokenPAP?: boolean,
    isFromInsufficientFunds?: boolean,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentMethodSelection',
  props: {
    user: null,
    userBalance: null,
    transactionObject: null,
    initiallyChosenTokenPAP: { type: Boolean, default: false },
    isFromInsufficientFunds: { type: Boolean, default: false }
  },
  emits: ['onPaymentMethodSelected', 'onStartLoading', 'onStopLoading', 'onClickBack'],
  setup(__props: any, { emit: $emit }) {

const props = __props as { user: MetaUser, userBalance: UserBalance, transactionObject: MunicipalityTx | Web3MunicipalityTx, initiallyChosenTokenPAP: boolean, isFromInsufficientFunds: boolean };

const context = useContext();
const isMobile = useMobile();



const ALWAYS_AVAILABLE_PAYMENT_METHODS: PaymentMethodOption[] = [
    { title: context.i18n.t('Web3 Wallet') as string, value: PaymentMethodEnum.WEB3_WALLET, disabled: false }
];

const USDT_FEE_VALUE = 3;



const {
    user,
    userBalance,
    transactionObject,
    initiallyChosenTokenPAP,
    isFromInsufficientFunds,
} = toRefs(props);

const isDirty = ref(false);
const showInsufficientFundsErrorText = ref(false);
const selectedPaymentMethod = ref(PaymentMethodEnum.WEB3_WALLET);
const paymentMethodOptions = ref(ALWAYS_AVAILABLE_PAYMENT_METHODS);
const specialConditions = ref<PaymentSpecialConditions>({
    isGasBack: false,
    mustUseUtility: false,
    mustUseGymnet: false
});

const handlePaymentMethodSelected = () => {
    $emit('onPaymentMethodSelected', selectedPaymentMethod.value, specialConditions.value);
}

const handleSetSelectedCurrency = async () => {
    if (selectedPaymentMethod.value === PaymentMethodEnum.PAP_BALANCE) {
        await transactionObject.value.updateSelectedCurrency(true);
    } else {
        await transactionObject.value.updateSelectedCurrency(false);
    }
}

const handleMethodChange = () => {
    isDirty.value = true;
    showInsufficientFundsErrorText.value = false;
    handleSetSelectedCurrency();
}

const updatePaymentMethods = () => {
    specialConditions.value.isGasBack = false;
    specialConditions.value.mustUseUtility = false;
    specialConditions.value.mustUseGymnet = false;
    const totalBUSDPrice = transactionObject.value.priceInfo.totalAmount.value;
    const newPaymentMethods = [...ALWAYS_AVAILABLE_PAYMENT_METHODS];

    if (user.value.isFullyWeb2User && userBalance.value) {
        const canUseGymnetOrUtility = transactionObject.value.canUseGymnetOrUtility;

        const bnbBalance = userBalance.value.bnb.value;
        const usdtBalance = userBalance.value.usdt.value;
        const utilityBalance = userBalance.value.utility.value;
        const gymnetBalance = userBalance.value.gymnet.value;

        const gasFee = transactionObject.value.priceInfo.gasAmount.value;
        const useUtilityGymnetBalance_USDT_Price = transactionObject.value.priceInfo.splitAmountUtility?.usdt.value || 0;
        const useUtilityGymnetBalance_GYMNET_Price = transactionObject.value.priceInfo.splitAmountUtility?.gymnet.value || 0;

        const isGasFeeSufficient = bnbBalance >= gasFee;
        const isTotalBUSDSufficient = usdtBalance >= totalBUSDPrice;
        const isTotalBUSDSufficientWithGasFee = usdtBalance >= totalBUSDPrice + USDT_FEE_VALUE;
        const isUtilityBUSDSufficient = usdtBalance >= useUtilityGymnetBalance_USDT_Price;
        const isUtilityBUSDSufficientWithGasFee = usdtBalance >= useUtilityGymnetBalance_USDT_Price + USDT_FEE_VALUE;

        const isUseUtilityGymnetBalance_UtilitySufficient = utilityBalance >= useUtilityGymnetBalance_GYMNET_Price;
        const isUseUtilityGymnetBalance_GYMNETSufficient = gymnetBalance >= useUtilityGymnetBalance_GYMNET_Price;

        const internalMethod = { title: context.i18n.t('Internal wallet') as string, value: PaymentMethodEnum.INTERNAL };

        let disabled = false;
        let disabledReason = null;

        const textOR = (context.i18n.t('Or') as string).toLowerCase();

        if (isTotalBUSDSufficient) {
            if (!isGasFeeSufficient) {
                if (!isTotalBUSDSufficientWithGasFee) {
                    if (canUseGymnetOrUtility && (isUseUtilityGymnetBalance_UtilitySufficient || isUseUtilityGymnetBalance_GYMNETSufficient)) {
                        if (!isGasFeeSufficient) {
                            specialConditions.value.isGasBack = true;
                        }
                        if (isUseUtilityGymnetBalance_UtilitySufficient && transactionObject.value.canUseUtility) {
                            specialConditions.value.mustUseUtility = true;
                        }
                        if (isUseUtilityGymnetBalance_GYMNETSufficient) {
                            specialConditions.value.mustUseGymnet = true;
                        }
                    } else {
                        disabled = true;
                        disabledReason = context.i18n.t('Please note that AMOUNT_CURRENCY is required for gas fee', {
                            amount_currency: USDT_FEE_VALUE + ' USDT ' + textOR + ' ' + gasFee + ' BNB',
                        });
                    }
                } else {
                    specialConditions.value.isGasBack = true;
                }
            }
        } else {
            if ((!isUseUtilityGymnetBalance_UtilitySufficient && !isUseUtilityGymnetBalance_GYMNETSufficient) || !canUseGymnetOrUtility) {
                disabled = true;
                disabledReason = context.i18n.t('Please note that AMOUNT worth of CURRENCY is required to complete the transaction.', {
                    amount: toCurrencyFormat(totalBUSDPrice),
                    currency: 'USDT',
                });
            } else {
                if (!isUtilityBUSDSufficient) {
                    const usdt_V = toCurrencyFormat(useUtilityGymnetBalance_USDT_Price);
                    const gym_V = toCurrencyFormat(useUtilityGymnetBalance_GYMNET_Price);
                    disabled = true;
                    disabledReason = context.i18n.t('Please note that AMOUNT worth of CURRENCY is required to complete the transaction.', {
                        amount: toCurrencyFormat(totalBUSDPrice),
                        currency: `USDT ${textOR} ${usdt_V} USDT ${context.i18n.t('and')} ${gym_V} Utility ${textOR} GYMNET`,
                    });
                } else {
                    if (!isGasFeeSufficient) {
                        if (!isUtilityBUSDSufficientWithGasFee) {
                            disabled = true;
                            disabledReason = context.i18n.t('Please note that AMOUNT_CURRENCY is required for gas fee', {
                                amount_currency: USDT_FEE_VALUE + ' USDT ' + textOR + ' ' + gasFee + ' BNB',
                            });
                        } else {
                            if (isUseUtilityGymnetBalance_UtilitySufficient && transactionObject.value.canUseUtility) {
                                specialConditions.value.mustUseUtility = true;
                            }
                            if (isUseUtilityGymnetBalance_GYMNETSufficient) {
                                specialConditions.value.mustUseGymnet = true;
                            }
                            specialConditions.value.isGasBack = true;
                        }
                    } else {
                        if (isUseUtilityGymnetBalance_UtilitySufficient && transactionObject.value.canUseUtility) {
                            specialConditions.value.mustUseUtility = true;
                        }
                        if (isUseUtilityGymnetBalance_GYMNETSufficient) {
                            specialConditions.value.mustUseGymnet = true;
                        }
                    }
                }
            }
        }
        Object.assign(internalMethod, { disabled, disabledReason });
        if (disabled) {
            if (selectedPaymentMethod.value === PaymentMethodEnum.INTERNAL && !isFromInsufficientFunds.value) {
                selectedPaymentMethod.value = newPaymentMethods[0].value;
            }
            newPaymentMethods.push(internalMethod);
        } else {
            if (!isDirty.value && !isFromInsufficientFunds.value) {
                selectedPaymentMethod.value = internalMethod.value;
            }
            newPaymentMethods.unshift(internalMethod);
        }
    }

    if (transactionObject.value.canUsePAP && userBalance.value) {
        const papBalanceMethod = { title: context.i18n.t('PAP Balance') as string, value: PaymentMethodEnum.PAP_BALANCE };
        const papBalance = userBalance.value.pap.value;
        const isTotalPAPSufficient = papBalance >= totalBUSDPrice;
        let disabled = !isTotalPAPSufficient;
        let disabledReason: any = '';

        if (!isTotalPAPSufficient) {
            disabledReason = context.i18n.t('Please note that AMOUNT worth of CURRENCY is required to complete the transaction.', {
                amount: toCurrencyFormat(totalBUSDPrice),
                currency: 'PAP',
            });
        }

        Object.assign(papBalanceMethod, { disabled, disabledReason });

        if (disabled) {
            if (selectedPaymentMethod.value === PaymentMethodEnum.PAP_BALANCE && !isFromInsufficientFunds.value) {
                selectedPaymentMethod.value = newPaymentMethods[0].value;
            }
            newPaymentMethods.push(papBalanceMethod);
        } else {
            newPaymentMethods.unshift(papBalanceMethod);

            if (initiallyChosenTokenPAP.value && !isDirty.value && !isFromInsufficientFunds.value) {
                selectedPaymentMethod.value = papBalanceMethod.value;
            }
        }
    }

    paymentMethodOptions.value = newPaymentMethods;
    handleSetSelectedCurrency();
}

const getBalanceValues = (balances: UserBalance | null) => {
    if (!balances) return '';
    let valuesString = '';
    const balanceCurrencies = [
        OrderCurrency.BNB,
        OrderCurrency.USDT,
        OrderCurrency.GYMNET,
        OrderCurrency.UTILITY,
        OrderCurrency.PAP
    ];
    balanceCurrencies.forEach((currency: string) => {
        valuesString += balances[currency].value;
    });
    return valuesString;
}

onMounted(() => {
    setTimeout(() => {
        showInsufficientFundsErrorText.value = !!isFromInsufficientFunds.value;
    }, 100);
})

watch(userBalance, (newBalance, oldBalance) => {
    if (getBalanceValues(newBalance) !== getBalanceValues(oldBalance) && getBalanceValues(userBalance.value)) {
        updatePaymentMethods();
    }
}, { immediate: true });


return { __sfc: true,context, isMobile, props, ALWAYS_AVAILABLE_PAYMENT_METHODS, USDT_FEE_VALUE, $emit, user, userBalance, transactionObject, initiallyChosenTokenPAP, isFromInsufficientFunds, isDirty, showInsufficientFundsErrorText, selectedPaymentMethod, paymentMethodOptions, specialConditions, handlePaymentMethodSelected, handleSetSelectedCurrency, handleMethodChange, updatePaymentMethods, getBalanceValues, PaymentMethodEnum }
}

})